import { useEffect, useRef, useState } from 'react'

import type { ChartApi } from '@/components/PriceChart'
import { parseDecimalInput } from '@/lib/numbers'

/**
 * Niveaux déplaçables posés sur le graphique.
 *
 * Ce que ces lignes montrent est particulier à cette application : **un même
 * niveau de prix a deux rôles à la fois**. Comme l'ordre part inversé, le stop
 * que vous placez devient la cible de l'ordre envoyé, et votre cible devient
 * son stop. Chaque ligne porte donc deux étiquettes, et celle qui correspond au
 * stop réel affiche le montant risqué.
 *
 * Réalisation : les poignées sont du HTML positionné au-dessus du canevas,
 * plutôt que dessinées dedans. Deux raisons :
 *
 * - une cible tactile de 44 px, impossible à obtenir sur une ligne d'un pixel ;
 * - `pointerdown/move/up` fonctionne à l'identique à la souris et au doigt,
 *   sans code séparé pour le tactile.
 *
 * **Le calque ne capte les événements que sur les bandes des niveaux.** Une
 * version précédente le faisait sur toute sa surface : le canevas ne recevait
 * alors plus rien, et ni la molette, ni le glissement, ni le pincement ne
 * fonctionnaient. Le graphique paraissait figé alors que la bibliothèque
 * savait parfaitement zoomer.
 */

/** Hauteur de la bande sensible autour d'une ligne, en pixels. */
const BAND_HEIGHT = 44

export interface Level {
  id: 'stop' | 'target'
  /** Prix du niveau. */
  price: number
  /** Distance en pips par rapport au prix d'entrée. */
  pips: number
  label: string
  /** Second rôle du même niveau, une fois l'ordre inversé. */
  mirrorLabel: string
  color: string
  /** Montant associé, déjà formaté (risque ou gain). */
  amount: string | null
  /** Vrai si ce niveau est le stop de l'ordre réellement envoyé. */
  isRealStop: boolean
}

export function ChartLevels({
  api,
  entryPrice,
  levels,
  pipSize,
  digits,
  onDrag,
  height,
  direction,
}: {
  api: ChartApi | null
  entryPrice: number | null
  levels: Level[]
  pipSize: number
  digits: number
  /**
   * Appelé pendant le glissement.
   *
   * `above` dit si le niveau a été lâché au-dessus du prix d'entrée. C'est ce
   * qui permet au parent d'en déduire le sens : un stop au-dessus de l'entrée
   * ne peut appartenir qu'à une vente, un stop en dessous qu'à un achat.
   */
  onDrag: (id: Level['id'], pips: number, above: boolean) => void
  height: number
  /**
   * Sens de l'ordre, écrit à côté du prix d'entrée.
   *
   * Ajouté après une confusion réelle : sur un achat, la ligne du HAUT porte le
   * stop du COURTIER (l'ordre part inversé). En lisant « Stop Loss » en rouge
   * au-dessus du prix, on en déduit naturellement une vente — alors que la
   * saisie est bien un achat. Le sens est donc écrit noir sur blanc, au lieu
   * d'être déduit d'une couleur.
   */
  direction?: string
}) {
  const [, forceRedraw] = useState(0)
  const [held, setHeld] = useState<Level['id'] | null>(null)
  // Saisie au clavier de la distance exacte. Le glissement suffit pour
  // approcher un niveau ; il ne permet pas de poser un stop à 30,0 pips pile,
  // et c'est souvent ce qu'on veut.
  const [editing, setEditing] = useState<Level['id'] | null>(null)
  const [draft, setDraft] = useState('')
  /*
   * Effacement au repos.
   *
   * Les deux étiquettes complètes — rôle, distance, montant, contrepartie chez
   * le courtier — sont indispensables au moment où l'on pose un niveau, et
   * seulement là. Affichées en permanence, ce sont deux bandeaux opaques posés
   * en travers des bougies : on ne regarde plus le marché, on regarde des
   * chiffres qu'on a déjà lus.
   *
   * Au repos ne subsistent donc que le trait et une pastille de quelques
   * pixels portant la distance. Tout revient dès qu'on s'approche du niveau
   * (survol), qu'on le tient, qu'on le saisit au clavier — et un court moment
   * après l'avoir lâché, le temps de vérifier ce qu'on vient de poser.
   *
   * La révélation est COMMUNE aux deux niveaux : resserrer son stop ramène la
   * cible avec lui, et c'est justement ce déplacement-là qu'il faut voir.
   */
  const [flash, setFlash] = useState(false)
  /*
   * Niveaux survolés, par identifiant plutôt qu'un simple booléen.
   *
   * Les bandes sensibles font 44 px : dès que deux niveaux se rapprochent,
   * elles se recouvrent. Le pointeur entre alors dans la seconde AVANT de
   * quitter la première, et la sortie — arrivant après l'entrée — remettait
   * un booléen à faux alors que le doigt était toujours sur une bande. Les
   * étiquettes s'éteignaient en plein réglage.
   *
   * Compter les identifiants règle le recouvrement, et se répare tout seul :
   * un identifiant qui ne correspond plus à aucun niveau ne compte pas. Une
   * sortie perdue — un niveau qui disparaît sous le pointeur — ne peut donc
   * pas laisser les étiquettes allumées pour toujours.
   */
  const [hoveredIds, setHoveredIds] = useState<Level['id'][]>([])
  const flashTimer = useRef<number | null>(null)
  const dragging = useRef<Level['id'] | null>(null)
  // Référence du conteneur de l'overlay : c'est par rapport à LUI que les
  // ordonnées doivent être mesurées. Se fier au parent immédiat de la poignée
  // donnerait une coordonnée relative à la ligne elle-même, qui est déjà
  // décalée — l'erreur se voit tout de suite : un glissement de quelques
  // pixels produit des centaines de pips.
  const overlay = useRef<HTMLDivElement>(null)
  // Dernière géométrie dessinée, et les valeurs vivantes que la boucle
  // d'animation doit consulter sans figer celles du rendu qui l'a créée.
  const geometry = useRef('')
  const snapshot = useRef({ api, levels, entryPrice })
  snapshot.current = { api, levels, entryPrice }

  // Toute modification d'une distance révèle les deux étiquettes, puis les
  // laisse s'effacer. `pipsKey` et non l'objet : `levels` est reconstruit à
  // chaque cotation, comparer les références déclencherait en permanence.
  const pipsKey = levels.map((level) => `${level.id}:${level.pips}`).join('|')
  const previousPips = useRef<string | null>(null)

  useEffect(() => {
    if (previousPips.current === null) {
      // Premier rendu : on ne révèle rien, on ne vient de rien décider.
      previousPips.current = pipsKey
      return
    }
    if (previousPips.current === pipsKey) return
    previousPips.current = pipsKey
    if (flashTimer.current !== null) window.clearTimeout(flashTimer.current)
    setFlash(true)
    flashTimer.current = window.setTimeout(() => setFlash(false), 1800)
  }, [pipsKey])

  useEffect(
    () => () => {
      if (flashTimer.current !== null) window.clearTimeout(flashTimer.current)
    },
    [],
  )

  /*
   * Le graphique se déplace tout seul (zoom, défilement, nouvelles bougies) :
   * les poignées doivent suivre. On REGARDE donc à chaque image — mais on ne
   * redessine que si quelque chose a réellement bougé.
   *
   * Sans ce filtre, React refaisait le rendu soixante fois par seconde sur un
   * graphique parfaitement immobile, pour réafficher deux fois la même chose.
   * Le calque des tracés faisant de même, le téléphone traitait cent vingt
   * rendus par seconde en permanence : la batterie fondait, l'appareil
   * chauffait, et tout le reste de l'interface — défilement, saisie, passage
   * d'onglet — se traînait derrière ce travail inutile.
   *
   * Comparer les ordonnées coûte deux appels de conversion ; c'est sans
   * commune mesure avec un rendu React.
   */
  useEffect(() => {
    let frame = 0
    const tick = () => {
      const current = snapshot.current
      let signature = ''
      if (current.api && current.entryPrice !== null) {
        signature = String(Math.round(current.api.priceToY(current.entryPrice) ?? -1))
        for (const level of current.levels) {
          signature += `|${Math.round(current.api.priceToY(level.price) ?? -1)}`
        }
      }
      if (signature !== geometry.current) {
        geometry.current = signature
        forceRedraw((n) => (n + 1) % 1000)
      }
      frame = requestAnimationFrame(tick)
    }
    frame = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(frame)
  }, [])

  if (!api || entryPrice === null) return null

  const entryY = api.priceToY(entryPrice)

  /** Vrai quand les étiquettes complètes doivent être lisibles. */
  const hovered = hoveredIds.some((id) => levels.some((level) => level.id === id))
  const revealed = held !== null || hovered || editing !== null || flash


  /*
   * L'étiquette du prix d'entrée s'efface quand un niveau la serre de trop
   * près.
   *
   * Elle se pose juste au-dessus de sa ligne, et l'étiquette du niveau HAUT
   * se pose juste au-dessus de la sienne : dès que ce niveau descend à
   * quelques pixels de l'entrée — un stop de quinze pips sur un H4, où tout
   * est tassé — les deux se chevauchent et plus rien ne se lit. On garde
   * alors la ligne pointillée, qui suffit à situer l'entrée, et on laisse la
   * place au niveau : c'est lui qu'on est en train de régler. Le prix reste
   * lisible sur l'échelle de droite, et le sens sur le bouton d'ordre.
   */
  const entryLabelCrowded =
    entryY !== null &&
    levels.some((level) => {
      const y = api.priceToY(level.price)
      // L'étiquette d'un niveau descend jusqu'à sa propre ligne, celle de
      // l'entrée monte de dix-huit pixels au-dessus de la sienne. Le seuil
      // suit donc la taille réelle de l'étiquette : dépliée, elle occupe
      // beaucoup plus de place que la pastille de repos.
      return y !== null && entryY - y > 0 && entryY - y < (revealed ? 24 : 16)
    })

  /** Niveau dont la ligne est la plus proche d'une ordonnée écran. */
  function nearestLevel(clientY: number): Level['id'] | null {
    const bounds = overlay.current?.getBoundingClientRect()
    if (!bounds || !api) return null

    let best: { id: Level['id']; distance: number } | null = null
    for (const level of levels) {
      const y = api.priceToY(level.price)
      if (y === null) continue
      const distance = Math.abs(bounds.top + y - clientY)
      if (best === null || distance < best.distance) best = { id: level.id, distance }
    }
    return best?.id ?? null
  }

  function startDrag(event: React.PointerEvent) {
    // On choisit le niveau le plus proche du point touché, et non celui dont
    // la bande a reçu l'événement : quand deux lignes sont à quelques pixels
    // l'une de l'autre — ce qui arrive dès qu'on regarde en H4 — les zones se
    // recouvrent et c'est la mauvaise qui répondait.
    const id = nearestLevel(event.clientY)
    if (id === null) return

    event.preventDefault()
    // La capture du pointeur garde le glissement actif même si le doigt sort
    // de la bande : sans elle, la ligne « lâcherait » dès qu'on va trop vite.
    // Elle échoue si le pointeur n'est plus actif (relâché entre-temps, ou
    // événement synthétique) : ce n'est pas une raison pour perdre le
    // glissement, qui fonctionne aussi sans capture tant que le doigt reste
    // sur la bande.
    try {
      event.currentTarget.setPointerCapture(event.pointerId)
    } catch {
      /* capture indisponible : on continue sans. */
    }
    dragging.current = id
    setHeld(id)
  }

  function moveDrag(event: React.PointerEvent) {
    if (dragging.current === null || !api || entryPrice === null) return
    const bounds = overlay.current?.getBoundingClientRect()
    if (!bounds) return

    const price = api.yToPrice(event.clientY - bounds.top)
    if (price === null) return

    // La distance est toujours positive : c'est le rôle du niveau, pas son
    // signe, qui dit s'il est au-dessus ou en dessous du prix d'entrée. Le
    // côté est transmis à part, il détermine le sens de l'ordre.
    const pips = Math.abs(price - entryPrice) / pipSize
    onDrag(dragging.current, Math.max(Math.round(pips * 10) / 10, 0.1), price > entryPrice)
  }

  /**
   * Applique la distance saisie au clavier.
   *
   * Le côté du niveau est CONSERVÉ : taper « 30 » déplace la ligne à trente
   * pips du prix d'entrée, du côté où elle se trouve déjà. Le sens de l'ordre
   * se change en faisant glisser la ligne de l'autre côté, pas en tapant un
   * nombre — un chiffre ne dit rien d'un sens.
   */
  /**
   * Décale la distance d'un niveau d'un pip, sans passer par le clavier.
   *
   * Régler un stop au pip près demandait soit un glissement précis sur une
   * ligne qui suit le marché, soit la saisie d'un nombre au clavier. Deux
   * boutons valent mieux pour le geste le plus courant : « un peu plus loin ».
   */
  function nudge(level: Level, delta: number) {
    if (entryPrice === null) return
    const pips = Math.max(Math.round((level.pips + delta) * 10) / 10, 0.1)
    setDraft(String(pips))
    onDrag(level.id, pips, level.price > entryPrice)
  }

  function commit(level: Level) {
    setEditing(null)
    const value = parseDecimalInput(draft)
    if (value === null || entryPrice === null) return
    const pips = Math.max(Number(value), 0.1)
    if (!Number.isFinite(pips)) return
    onDrag(level.id, Math.round(pips * 10) / 10, level.price > entryPrice)
  }

  function endDrag(event: React.PointerEvent) {
    if (dragging.current !== null) {
      try {
        event.currentTarget.releasePointerCapture(event.pointerId)
      } catch {
        /* rien à libérer si la capture n'avait pas pris. */
      }
      dragging.current = null
      setHeld(null)
      // Un appui sans déplacement ne change aucune distance, donc ne
      // déclenche pas la révélation par changement de valeur. Il montre
      // pourtant une intention : toucher un niveau, c'est vouloir le lire.
      if (flashTimer.current !== null) window.clearTimeout(flashTimer.current)
      setFlash(true)
      flashTimer.current = window.setTimeout(() => setFlash(false), 1800)
    }
  }

  return (
    // `z-10` : le canevas du graphique se dessine par-dessus tout ce qui n'a
    // pas de plan explicite, et masquerait les niveaux.
    // `pointer-events-none` : tout ce que le calque ne réclame pas explicitement
    // redescend au canevas, qui garde ainsi molette, pincement et glissement.
    <div
      ref={overlay}
      className="pointer-events-none absolute inset-0 z-10"
      style={{ height }}
    >
      {/* Prix d'entrée : repère fixe, non déplaçable. */}
      {entryY !== null && (
        <div
          className="absolute left-0 right-0 border-t border-dashed border-ink-muted/60"
          style={{ top: entryY }}
        >
          {!entryLabelCrowded && (
            <span className="num absolute -top-4 left-1 flex max-w-[calc(100%-7.5rem)] items-center gap-1.5 truncate rounded bg-elevated px-1 text-[10px] text-ink-muted">
              <span className="shrink-0">entrée {entryPrice.toFixed(digits)}</span>
              {direction && (
                <span className="truncate font-semibold text-ink-strong">{direction}</span>
              )}
            </span>
          )}
        </div>
      )}

      {levels.map((level) => {
        const y = api.priceToY(level.price)
        if (y === null) return null

        /*
         * De quel côté écrire l'étiquette : au-dessus de la ligne haute, en
         * dessous de la ligne basse. Les deux s'éloignent ainsi l'une de
         * l'autre, et le prix d'entrée garde sa place entre elles.
         *
         * Ce côté se déduit de la POSITION du niveau, jamais de son rôle.
         * Il se déduisait du rôle : « ma cible » toujours au-dessus de sa
         * ligne, « mon stop » toujours en dessous. Vrai sur un achat, faux
         * sur une vente — où le stop passe au-dessus de l'entrée et la cible
         * en dessous. Les deux étiquettes se retournaient alors vers le
         * milieu et se superposaient l'une à l'autre, sur celle du prix
         * d'entrée par-dessus le marché : trois textes empilés, illisibles,
         * à l'endroit précis où l'on vérifie son risque avant d'appuyer.
         */
        const higher = level.price > entryPrice

        return (
          <div key={level.id} className="absolute left-0 right-0" style={{ top: y }}>
            {/* Le trait reste visible au repos — c'est lui le niveau — mais
                s'efface assez pour que les bougies passent devant. */}
            <div
              className="pointer-events-none absolute left-0 right-0 transition-opacity duration-200"
              style={{
                borderTopColor: level.color,
                borderTopWidth: held === level.id ? 2 : 1,
                borderTopStyle: 'solid',
                opacity: revealed ? 1 : 0.55,
              }}
            />

            {/*
              Une seule étiquette par ligne, portant les deux rôles. Deux
              lignes de texte par niveau se chevauchaient dès que les deux
              niveaux se rapprochaient, et plus rien n'était lisible.
              Le niveau haut s'affiche au-dessus de sa ligne, le bas en
              dessous : les étiquettes s'éloignent au lieu de se superposer.
            */}
            {editing === level.id ? (
              <form
                onSubmit={(event) => {
                  event.preventDefault()
                  commit(level)
                }}
                className="pointer-events-auto absolute left-1 flex items-center gap-1 rounded px-1 py-0.5"
                style={{ backgroundColor: level.color, top: higher ? -20 : 2 }}
              >
                {/* `onMouseDown` empêché : sans cela, appuyer sur « − » fait
                    d'abord perdre le focus au champ, ce qui valide et referme
                    le formulaire avant que le bouton n'ait agi. */}
                <button
                  type="button"
                  aria-label="Un pip de moins"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => nudge(level, -1)}
                  className="h-7 w-7 shrink-0 rounded bg-[#0b0e14]/70 text-[14px] font-bold text-ink"
                >
                  −
                </button>
                <input
                  // `inputMode="decimal"` ouvre le pavé numérique du téléphone.
                  // La virgule des claviers français est acceptée à la lecture.
                  inputMode="decimal"
                  type="text"
                  value={draft}
                  autoFocus
                  onChange={(event) => setDraft(event.target.value)}
                  onBlur={() => commit(level)}
                  onKeyDown={(event) => {
                    if (event.key === 'Escape') setEditing(null)
                  }}
                  className="num w-12 rounded bg-[#0b0e14] px-1 text-center text-[12px] text-ink outline-none"
                />
                <button
                  type="button"
                  aria-label="Un pip de plus"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => nudge(level, 1)}
                  className="h-7 w-7 shrink-0 rounded bg-[#0b0e14]/70 text-[14px] font-bold text-ink"
                >
                  +
                </button>
                <span className="text-[10px] font-bold text-[#0b0e14]">pips</span>
              </form>
            ) : (
              <button
                type="button"
                // Cliquable, contrairement au reste du calque : c'est le seul
                // élément qui doit intercepter le pointeur, pour ne pas voler
                // le glissement à la ligne ni la molette au graphique.
                onPointerDown={(event) => event.stopPropagation()}
                /*
                  Au RELÂCHEMENT, et non à l'appui : un balayage de la page qui
                  démarrait sur l'étiquette ouvrait la saisie et faisait monter
                  le clavier du téléphone, pour un geste qui ne la visait pas.
                */
                onClick={() => {
                  setDraft(String(level.pips))
                  setEditing(level.id)
                }}
                /*
                  Deux lignes courtes, et une largeur bornée.
                  ----------------------------------------------------------
                  L'étiquette tenait sur une seule ligne insécable : « MON
                  STOP 20p · +41.28 EUR (cible du courtier) » fait plus de
                  400 px. Sur un téléphone elle traversait tout le graphique,
                  passait sous la poignée de prix et sortait de l'écran — le
                  rôle chez le courtier, justement ce qu'on a besoin de lire
                  ici, était la partie coupée. Et deux barres pleine largeur
                  recouvraient les bougies qu'on essayait de regarder.

                  Le rôle passe donc sur une seconde ligne, et la largeur
                  s'arrête avant la poignée de droite.
                */
                className={`num pointer-events-auto absolute left-1 max-w-[calc(100%-7.5rem)] rounded text-left leading-tight transition-all duration-200 ${
                  revealed ? 'px-1.5 py-0.5' : 'px-1'
                }`}
                style={
                  revealed
                    ? {
                        backgroundColor: level.color,
                        color: '#0b0e14',
                        top: higher ? -28 : 4,
                      }
                    : {
                        // Au repos : pas de pavé de couleur, juste la distance
                        // écrite dans la teinte du niveau sur un fond sombre
                        // à peine marqué. Assez pour situer, trop peu pour
                        // gêner la lecture des bougies.
                        backgroundColor: 'rgba(11, 14, 20, 0.78)',
                        color: level.color,
                        top: higher ? -16 : 3,
                      }
                }
                title="Saisir la distance exacte"
              >
                <span className="block truncate text-[10px] font-bold">
                  {revealed ? (
                    <>
                      {level.label} {level.pips}p{level.amount && ` · ${level.amount}`}
                    </>
                  ) : (
                    `${level.pips}p`
                  )}
                </span>
                {revealed && level.mirrorLabel && (
                  <span className="block truncate text-[9px] font-normal opacity-80">
                    {level.mirrorLabel}
                  </span>
                )}
              </button>
            )}

            {/*
              Bande sensible : la SEULE zone du calque qui capte le pointeur.
              Elle fait 44 px de haut, comme toute cible tactile, et
              `touch-none` empêche la page de défiler pendant le glissement.

              **Elle ne traverse plus l'écran sur téléphone.** Deux bandes de
              44 px en largeur pleine, c'est un quart de la hauteur du
              graphique où le doigt ne peut plus ni faire défiler la page, ni
              déplacer le graphique, ni pincer pour zoomer — et où un simple
              geste de défilement déplaçait le stop sans qu'on l'ait demandé.
              Un ordre modifié par mégarde, c'est précisément ce qu'il faut
              rendre impossible.

              Elle se limite donc au tiers droit, là où se trouve déjà
              l'étiquette de prix : on attrape la ligne par sa poignée, comme
              sur n'importe quelle plateforme. À la souris (`md:`), toute la
              ligne redevient saisissable — un clic est intentionnel, il ne
              risque pas d'être un défilement.
            */}
            <div
              onPointerDown={startDrag}
              onPointerMove={moveDrag}
              onPointerUp={endDrag}
              onPointerCancel={endDrag}
              // S'approcher d'un niveau suffit à le faire parler : à la
              // souris, on survole avant de saisir. Au doigt, l'entrée de
              // pointeur accompagne l'appui — même résultat.
              onPointerEnter={() =>
                setHoveredIds((current) =>
                  current.includes(level.id) ? current : [...current, level.id],
                )
              }
              onPointerLeave={() =>
                setHoveredIds((current) => current.filter((id) => id !== level.id))
              }
              className="pointer-events-auto absolute left-[62%] right-0 -translate-y-1/2 cursor-ns-resize touch-none md:left-0"
              style={{ height: BAND_HEIGHT }}
            />

            {/* Poignée : l'étiquette de prix, sur l'échelle de droite. La
                double flèche dit que la ligne se prend ici — sans elle, rien
                ne distingue une étiquette d'information d'une prise. */}
            <div className="pointer-events-none absolute right-0 flex h-touch w-24 -translate-y-1/2 items-center justify-end pr-1">
              <span
                className="num flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-bold transition-opacity duration-200"
                style={{
                  backgroundColor: level.color,
                  color: '#0b0e14',
                  outline: held === level.id ? '2px solid #ffffff' : 'none',
                  // Moins appuyée au repos, mais jamais effacée : c'est par
                  // elle qu'on attrape la ligne au doigt, elle doit rester
                  // trouvable sans avoir à la chercher.
                  opacity: revealed ? 1 : 0.75,
                }}
              >
                <span aria-hidden className="opacity-60">⇅</span>
                {level.price.toFixed(digits)}
              </span>
            </div>
          </div>
        )
      })}
    </div>
  )
}
